import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../core/data/redux/store';
import { createEsicConfiguration, updateEsicConfiguration, fetchEsicConfigurationList, deleteEsicConfiguration, } from '../../../../core/data/redux/payroll/ESIC/Master/Configuration/configurationActions';
import { Drawer, message } from 'antd';
import DeleteModal1 from '../../../../core/modals/deleteModal1';
import dayjs from 'dayjs';

const ConfigurationModal: React.FC<any> = ({
  handleCancel,
  isModalVisible,
  modalType,
  selectedRowData,
  esicData,
  setEsicData,
  setTableKey,
  setIsDeleteModalVisible,
  isDeleteModalVisible,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading } = useSelector((state: any) => state.esic.configuration);
  const { activeCompany } = useSelector((state: any) => state.companyDetails);

  const [errors, setErrors] = useState<any>({});

  const resetForm = () => {
    setEsicData({
      company_id: activeCompany?.company_name,
      esic_code: '',
      office_name: '',
      office_address: '',
      employer_contribution_rate: '',
      employee_contribution_rate: '',
      wage_limit: 0,
      effective_from: '',
      effective_to: '',
      status: 'active',
    });
    setErrors({});
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement> | any, field: string) => {
    const value = e.target ? e.target.value : e;
    setEsicData({ ...esicData, [field]: value });
    setErrors((prev: any) => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    let formIsValid = true;
    let newErrors: any = {};

    if (!esicData.company_id) newErrors.company_id = 'Company is required.';
    if (!esicData.esic_code) {
      newErrors.esic_code = 'ESIC Code is required.';
    } else if (!/^\d{2}-\d{8}-\d{1}-\d{2}-\d{4}$/.test(esicData.esic_code)) {
      newErrors.esic_code = 'Invalid ESIC Code format. Expected format: XX-XXXXXXXX-X-XX-XXXX';
    }
    if (!esicData.office_name) newErrors.office_name = 'Office Name is required.';
    if (!esicData.office_address) newErrors.office_address = 'Office Address is required.';
    if (esicData.employer_contribution_rate === undefined || esicData.employer_contribution_rate === '') {
      newErrors.employer_contribution_rate = 'Employer Contribution Rate is required.';
    } else if (Number(esicData.employer_contribution_rate) > 100) {
      newErrors.employer_contribution_rate = 'Employer Contribution Rate cannot be more than 100%.';
    }

    // Employee contribution validation
    if (esicData.employee_contribution_rate === undefined || esicData.employee_contribution_rate === '') {
      newErrors.employee_contribution_rate = 'Employee Contribution Rate is required.';
    } else if (Number(esicData.employee_contribution_rate) > 100) {
      newErrors.employee_contribution_rate = 'Employee Contribution Rate cannot be more than 100%.';
    }

    if (!esicData.effective_from) newErrors.effective_from = 'Effective From date is required.';
    if (!esicData.effective_to) newErrors.effective_to = 'Effective To date is required.';
    if (!esicData.status) newErrors.status = 'Status is required.';
    if (esicData.effective_from && esicData.effective_to) {
      const fromDate = dayjs(esicData.effective_from);
      const toDate = dayjs(esicData.effective_to);
      if (fromDate.isAfter(toDate)) {
        newErrors.effective_from = 'Effective From date cannot be after Effective To date.';
        newErrors.effective_to = 'Effective To date cannot be before Effective From date.';
        formIsValid = false;
      }
    }

    if (Object.keys(newErrors).length > 0) {
      formIsValid = false;
    }
    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    const payload = {
      ...esicData,
      effective_from: esicData.effective_from ? dayjs(esicData.effective_from).format("YYYY-MM-DD") : null,
      effective_to: esicData.effective_to ? dayjs(esicData.effective_to).format("YYYY-MM-DD") : null,
    };
    try {
      let result;
      if (modalType === 'create') {
        result = await dispatch(createEsicConfiguration(payload)).unwrap();
      } else if (modalType === 'edit' && selectedRowData) {
        result = await dispatch(
          updateEsicConfiguration({ id: selectedRowData.config_id, ...payload })
        ).unwrap();
      }
      if (result) {
        message.success(result.message || 'Success');
        await dispatch(fetchEsicConfigurationList());
        setTableKey((prev: any) => prev + 1);
        resetForm();
        handleCancel();
      }
    } catch (error: any) {
      message.error(error.error || 'An error occurred');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const result = await dispatch(deleteEsicConfiguration(id));
      if (result.meta.requestStatus === 'fulfilled') {
        message.success(result.payload.message);
        await dispatch(fetchEsicConfigurationList());
        setTableKey((prev: any) => prev + 1);
        setIsDeleteModalVisible(false);
      } else {
        message.warning(result.payload.error);
      }
    } catch {
      message.error('Something went wrong');
    }
  };

  return (
    <>
      <Drawer
        title={modalType === 'edit' ? 'Edit ESIC Configuration' : 'Add ESIC Configuration'}
        placement="right"
        width={600}
        onClose={() => {
          handleCancel();
          resetForm();
        }}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <form onSubmit={handleSubmit}>
          <div className="modal-body pb-0">
            {/* Company ID */}
            <div className="mb-3">
              <label className="form-label">Company <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={esicData.company_id}
                disabled
                onChange={(e) => handleInputChange(e, 'company_id')}
              />
              {errors.company_id && <div className="text-danger">{errors.company_id}</div>}
            </div>

            {/* ESIC Code */}
            <div className="mb-3">
              <label className="form-label">ESIC Code <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. 12-34567890-1-01-0001"
                value={esicData.esic_code}
                onChange={(e) => handleInputChange(e, 'esic_code')}
              />
              {errors.esic_code && <div className="text-danger">{errors.esic_code}</div>}
            </div>

            {/* Office Name */}
            <div className="mb-3">
              <label className="form-label">Office Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={esicData.office_name}
                onChange={(e) => handleInputChange(e, 'office_name')}
              />
              {errors.office_name && <div className="text-danger">{errors.office_name}</div>}
            </div>

            {/* Office Address */}
            <div className="mb-3">
              <label className="form-label">Office Address <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                value={esicData.office_address}
                onChange={(e) => handleInputChange(e, 'office_address')}
              />
              {errors.office_address && <div className="text-danger">{errors.office_address}</div>}
            </div>

            {/* Employer Contribution Rate */}
            <div className="mb-3">
              <label className="form-label">Employer Contribution Rate (%) <span className="text-danger">*</span></label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                value={esicData.employer_contribution_rate}
                onChange={(e) => handleInputChange(e, 'employer_contribution_rate')}
              />
              {errors.employer_contribution_rate && <div className="text-danger">{errors.employer_contribution_rate}</div>}
            </div>

            {/* Employee Contribution Rate */}
            <div className="mb-3">
              <label className="form-label">Employee Contribution Rate (%) <span className="text-danger">*</span></label>
              <input
                type="number"
                className="form-control"
                step="0.01"
                value={esicData.employee_contribution_rate}
                onChange={(e) => handleInputChange(e, 'employee_contribution_rate')}
              />
              {errors.employee_contribution_rate && <div className="text-danger">{errors.employee_contribution_rate}</div>}
            </div>

            {/* Wage Limit */}
            <div className="mb-3">
              <label className="form-label">Wage Limit </label>
              <input
                type="number"
                className="form-control"
                value={esicData.wage_limit}
                onChange={(e) => handleInputChange(e, 'wage_limit')}
              />
              {errors.wage_limit && <div className="text-danger">{errors.wage_limit}</div>}
            </div>

            {/* Effective From */}
            <div className="mb-3">
              <label className="form-label">Effective From <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                value={esicData.effective_from}
                onChange={(e) => handleInputChange(e, 'effective_from')}
              />
              {errors.effective_from && <div className="text-danger">{errors.effective_from}</div>}
            </div>

            {/* Effective To */}
            <div className="mb-3">
              <label className="form-label">Effective To <span className="text-danger">*</span></label>
              <input
                type="date"
                className="form-control"
                value={esicData.effective_to}
                onChange={(e) => handleInputChange(e, 'effective_to')}
              />
              {errors.effective_to && <div className="text-danger">{errors.effective_to}</div>}
            </div>

            {/* Status */}
            <div className="mb-3">
              <label className="form-label">Status <span className="text-danger">*</span></label>
              <select
                className="form-control"
                value={esicData.status}
                onChange={(e) => handleInputChange(e, 'status')}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              {errors.status && <div className="text-danger">{errors.status}</div>}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-white border me-2"
              onClick={() => {
                resetForm();
                handleCancel();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading && <span className="spinner-border spinner-border-sm me-2" />}
              {modalType === 'edit' ? 'Update Configuration' : 'Add Configuration'}
            </button>
          </div>
        </form>
      </Drawer>

      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() => selectedRowData?.config_id && handleDelete(selectedRowData.config_id)}
      />
    </>
  );
};

export default ConfigurationModal;
